import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";

interface NotificationItem {
  id: string;
  user_id: string;
  message?: string;
  amount?: number;
  description?: string;
  created_at: string;
  type: "carrier_test" | "charge";
  profiles?: { full_name: string | null; email: string | null } | null;
  is_read?: boolean;
}

interface ReviewCountResponse {
  carrier_count: number;
  charge_count: number;
  total: number;
  recent: NotificationItem[];
}

export default function AdminNotificationBell() {
  const [count, setCount] = useState(0);
  const [carrierCount, setCarrierCount] = useState(0);
  const [chargeCount, setChargeCount] = useState(0);
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [open, setOpen] = useState(false);
  const [hasNew, setHasNew] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const fetchNotifications = useCallback(async () => {
    try {
      const { data, error } = await supabase.functions.invoke("get-admin-review-count");
      if (!error && data) {
        const res = data as ReviewCountResponse;
        setCount(res.total);
        setCarrierCount(res.carrier_count);
        setChargeCount(res.charge_count);
        setNotifications(res.recent);
      }
    } catch (e) {
      console.error("알림 데이터 로딩 실패:", e);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();

    const channel = supabase
      .channel("admin-notifications")
      .on("broadcast", { event: "new-carrier-test" }, () => {
        fetchNotifications();
        setHasNew(true);
        setTimeout(() => setHasNew(false), 3000);
      })
      .on("broadcast", { event: "review-completed" }, () => {
        fetchNotifications();
      })
      .on("broadcast", { event: "new-charge-request" }, () => {
        fetchNotifications();
        setHasNew(true);
        setTimeout(() => setHasNew(false), 3000);
      })
      .on("broadcast", { event: "charge-reviewed" }, () => {
        fetchNotifications();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchNotifications]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const markAsRead = useCallback(async (item: NotificationItem) => {
    try {
      await supabase.functions.invoke("mark-notification-read", {
        body: { item_type: item.type, item_id: Number(item.id) },
      });
    } catch (e) {
      console.error("읽음 처리 실패:", e);
    }
  }, []);

  const handleClick = (item: NotificationItem) => {
    setOpen(false);
    markAsRead(item);
    if (item.type === "charge") {
      navigate("/admin/charges");
    } else {
      navigate("/admin/carrier-test-review?filter=pending");
    }
  };

  const handleOpenToggle = () => {
    setOpen(!open);
  };

  const formatAmount = (amount?: number) => {
    if (!amount) return "";
    return amount.toLocaleString("ko-KR") + "원";
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={handleOpenToggle}
        className={`relative w-9 h-9 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
          hasNew ? "bg-rose-500/20 text-rose-400" : "bg-white/[0.06] text-gray-400 hover:text-white hover:bg-white/[0.1]"
        }`}
      >
        <i className="ri-notification-3-line text-base"></i>
        {count > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center bg-rose-500 text-white text-[10px] font-bold rounded-full border-2 border-[#0a0a18]">
            {count > 99 ? "99+" : count}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-12 w-80 bg-[#0f0f1e] border border-white/[0.08] rounded-xl shadow-2xl overflow-hidden z-50">
          <div className="px-4 py-3 border-b border-white/[0.06] flex items-center justify-between">
            <h3 className="text-white text-sm font-semibold">
              관리자 알림
              {count > 0 && (
                <span className="text-rose-400 text-xs ml-1.5">{count}개</span>
              )}
            </h3>
            <div className="flex items-center gap-2">
              {carrierCount > 0 && (
                <span className="text-rose-400 text-xs font-medium bg-rose-500/10 px-2 py-0.5 rounded-full">
                  테스트 {carrierCount}개
                </span>
              )}
              {chargeCount > 0 && (
                <span className="text-amber-400 text-xs font-medium bg-amber-500/10 px-2 py-0.5 rounded-full">
                  충전 {chargeCount}개
                </span>
              )}
            </div>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <i className="ri-checkbox-circle-line text-2xl text-gray-600 mb-2"></i>
                <p className="text-gray-500 text-sm">새로운 알림이 없습니다</p>
              </div>
            ) : (
              notifications.map((n) => (
                <button
                  key={`${n.type}-${n.id}`}
                  onClick={() => handleClick(n)}
                  className={`w-full text-left px-4 py-3 transition-colors border-b border-white/[0.04] last:border-b-0 cursor-pointer ${
                    n.is_read
                      ? "bg-transparent hover:bg-white/[0.02]"
                      : "bg-white/[0.04] hover:bg-white/[0.06]"
                  }`}
                >
                  <div className="flex items-start gap-2.5">
                    <div className={`w-8 h-8 flex items-center justify-center rounded-lg shrink-0 mt-0.5 ${
                      n.is_read ? "bg-white/[0.04]" : n.type === "charge" ? "bg-amber-500/10" : "bg-rose-500/10"
                    }`}>
                      <i className={`${n.type === "charge" ? "ri-coin-line text-amber-400" : "ri-smartphone-line text-rose-400"} text-sm ${
                        n.is_read ? "opacity-40" : ""
                      }`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        {!n.is_read && (
                          <span className="w-1.5 h-1.5 rounded-full bg-rose-400 shrink-0"></span>
                        )}
                        <p className={`text-sm font-medium truncate ${
                          n.is_read ? "text-gray-500" : "text-white"
                        }`}>
                          {n.type === "charge" ? "크레딧 충전 요청" : "3사 테스트 발송"}
                          {n.type === "charge" && n.amount && (
                            <span className={`ml-1 ${n.is_read ? "text-amber-500/60" : "text-amber-400"}`}>
                              {formatAmount(n.amount)}
                            </span>
                          )}
                        </p>
                      </div>
                      <p className={`text-xs mt-0.5 ${n.is_read ? "text-gray-600" : "text-gray-500"}`}>
                        {n.profiles?.full_name || n.profiles?.email || "알 수 없는 사용자"}
                      </p>
                      <p className={`text-[10px] mt-1 ${n.is_read ? "text-gray-700" : "text-gray-600"}`}>
                        {new Date(n.created_at).toLocaleString("ko-KR")}
                      </p>
                    </div>
                    <i className={`ri-arrow-right-s-line text-sm shrink-0 mt-1 ${
                      n.is_read ? "text-gray-700" : "text-gray-600"
                    }`}></i>
                  </div>
                </button>
              ))
            )}
          </div>
          <div className="px-4 py-2.5 border-t border-white/[0.06] bg-white/[0.02]">
            <button
              onClick={() => {
                setOpen(false);
                navigate("/admin");
              }}
              className="w-full text-center text-gray-400 text-xs font-medium hover:text-white cursor-pointer transition-colors"
            >
              전체 알림 보기
            </button>
          </div>
        </div>
      )}
    </div>
  );
}